import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckSquare } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { MARK_STYLE, localIso, errMsg } from "./AttendanceTab";

const MARKS = [["P", "Present"], ["A", "Absent"], ["HD", "Half day"], ["L", "Leave"], ["", "Automatic"]];

function People({ rows, picked, setPicked }) {
  const all = picked.length === rows.length;
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">1 · People ({picked.length})</div>
        <button type="button" onClick={() => setPicked(all ? [] : rows.map((r) => r.user_id))} className="text-xs font-semibold text-red-700" data-testid="bulk-select-all-people">{all ? "Clear" : "Select all"}</button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto" data-testid="bulk-people">
        {rows.map((r) => {
          const on = picked.includes(r.user_id);
          return <button key={r.user_id} type="button" onClick={() => toggle(r.user_id)} className={`h-8 px-3 rounded-full text-xs font-semibold transition-colors ${on ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`bulk-person-${r.user_id}`}>{r.name}</button>;
        })}
      </div>
    </section>
  );
}

function Days({ data, days, setDays, offFor }) {
  const today = localIso();
  const lead = (new Date(`${data.days[0]}T00:00:00`).getDay() + 6) % 7;
  const open = data.days.filter((d) => d <= today);
  const toggle = (d) => setDays((x) => (x.includes(d) ? x.filter((y) => y !== d) : [...x, d]));
  const working = open.filter((d) => !data.holidays?.[d] && !offFor(d));
  return (
    <section>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold">2 · Days ({days.length})</div>
        <div className="flex gap-3 text-xs font-semibold text-red-700">
          <button type="button" onClick={() => setDays(working)} data-testid="bulk-days-working">Working days</button>
          <button type="button" onClick={() => setDays(open)} data-testid="bulk-days-all">All so far</button>
          <button type="button" onClick={() => setDays([])} className="text-slate-500" data-testid="bulk-days-clear">Clear</button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 mb-1">{["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}</div>
      <div className="grid grid-cols-7 gap-1" data-testid="bulk-days">
        {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
        {data.days.map((d) => {
          const on = days.includes(d), future = d > today, tag = data.holidays?.[d] ? "H" : offFor(d) ? "WO" : "";
          return (
            <button key={d} type="button" disabled={future} onClick={() => toggle(d)} title={data.holidays?.[d] || undefined}
              className={`h-10 rounded-lg flex flex-col items-center justify-center text-xs font-bold transition-colors disabled:opacity-30 ${on ? "bg-red-600 text-white" : tag ? MARK_STYLE[tag] : "bg-slate-50 text-slate-700 hover:bg-slate-100"}`}
              data-testid={`bulk-day-${d}`}>{Number(d.slice(8))}{tag && <span className="text-[8px] leading-none">{tag}</span>}</button>
          );
        })}
      </div>
    </section>
  );
}

export function BulkMarkDialog({ open, data, onClose, onDone }) {
  const [picked, setPicked] = useState([]);
  const [days, setDays] = useState([]);
  const [mark, setMark] = useState("P");
  const [skip, setSkip] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPicked([]); setDays([]); setMark("P"); setSkip(true); } }, [open]);
  const one = picked.length === 1 ? data?.rows.find((r) => r.user_id === picked[0]) : null;
  const offFor = useMemo(() => (d) => !!one && (one.weekly_off || []).includes((new Date(`${d}T00:00:00`).getDay() + 6) % 7), [one]);
  if (!data) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/hr/attendance/bulk", { user_ids: picked, dates: days, status: mark || null, skip_off: skip });
      toast.success(`Updated ${r.marked} day${r.marked === 1 ? "" : "s"}${r.skipped ? ` · skipped ${r.skipped} holiday / weekly off` : ""}`);
      onDone(); onClose();
    } catch (e) { toast.error(errMsg(e, "Could not mark")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg w-[calc(100vw-2rem)] sm:w-full max-h-[92vh] overflow-y-auto" data-testid="bulk-mark-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Bulk mark attendance</DialogTitle>
          <DialogDescription>Pick people and days, then choose one mark for all of them.</DialogDescription></DialogHeader>
        <div className="space-y-5">
          <People rows={data.rows} picked={picked} setPicked={setPicked} />
          <Days data={data} days={days} setDays={setDays} offFor={offFor} />
          <section>
            <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-bold mb-2">3 · Mark as</div>
            <div className="flex flex-wrap gap-1.5">
              {MARKS.map(([k, l]) => <button key={k || "auto"} type="button" onClick={() => setMark(k)} className={`h-9 px-3 rounded-full text-xs font-bold transition-all ${mark === k ? "ring-2 ring-slate-900 " : ""}${MARK_STYLE[k] || "bg-slate-100 text-slate-600"}`} data-testid={`bulk-mark-${k || "auto"}`}>{l}</button>)}
            </div>
            <label className="flex items-center gap-2 mt-3 text-sm text-slate-700"><input type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} className="h-4 w-4 accent-red-600" data-testid="bulk-skip-off" />Skip holidays and each person's weekly off</label>
          </section>
          <Button onClick={save} disabled={busy || !picked.length || !days.length} className="w-full h-11 bg-red-600 hover:bg-red-700 text-white" data-testid="bulk-mark-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckSquare className="h-4 w-4 mr-1.5" />}Mark {picked.length} {picked.length === 1 ? "person" : "people"} × {days.length} day{days.length === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
